'use client'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Zap, Target, Clock, ArrowRight, CheckCircle2 } from 'lucide-react'
import { StarIcon } from './medallions/star-icon'

export interface FocusAndQuestsCardProps {
  weakestPillar: { code: string; label: string; mastery: number }
  pillarSlam: { doneCodes: string[]; total: number }
  streakCurrent: number
  trainHref?: string      // default '/pt-play' (independent, chrome-free)
}

export function FocusAndQuestsCard({ weakestPillar, pillarSlam, streakCurrent, trainHref = '/pt-play' }: FocusAndQuestsCardProps) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" /> Your focus
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Weakest pillar */}
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-start gap-3">
            <Target className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold">Weakest pillar</div>
              <div className="text-sm font-semibold text-foreground">
                {weakestPillar.label} — {weakestPillar.mastery}%
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Train here to level up fastest.</div>
              <Button asChild size="sm" variant="secondary" className="mt-2 h-7 text-xs">
                <Link href={trainHref}>
                  Train {weakestPillar.code} <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Pillar Slam quest */}
        <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent p-3">
          <div className="flex items-start gap-3">
            <StarIcon size={22} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-widest text-amber-700 dark:text-amber-300 font-semibold">Weekly quest</span>
                <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-300">+1★ bonus</Badge>
              </div>
              <div className="text-sm font-semibold text-foreground">Pillar Slam</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">One round in each P.H.I.S.H. pillar this week.</div>
              <div className="mt-2 flex items-center gap-1.5">
                {['PF1', 'PF2', 'PF3', 'PF4', 'PF5'].map((c) => {
                  const done = pillarSlam.doneCodes.includes(c)
                  return (
                    <div
                      key={c}
                      className={
                        'flex-1 h-7 rounded-md border text-[10px] font-semibold flex items-center justify-center transition-colors ' +
                        (done
                          ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
                          : 'bg-muted/40 border-border text-muted-foreground')
                      }
                    >
                      {done ? <CheckCircle2 className="h-3 w-3 mr-0.5" /> : null}
                      {c}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Daily quest */}
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-widest text-primary font-semibold">Today</span>
                <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">+1★</Badge>
              </div>
              <div className="text-sm font-semibold text-foreground">Play one round today</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Keep your {streakCurrent}-day streak alive.</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
